import type {
  SearchParameterName,
  SearchParameters,
  SearchParameterValues,
} from 'src/domain';

/**
 * The change set that turns `from` into `to`: every value `to` states, and
 * `undefined` for anything only `from` states, which `with()` reads as a clear.
 *
 * Exists because the legacy search runs on two baselines. Filter edits are
 * dispatched to Redux before `search()` runs, so the parameters a search starts
 * from already differ from the last completed search — but it is the completed
 * search a scope change has to be compared against. A `Search` compares a
 * transition against itself, so the legacy path restores the completed search
 * and hands it the difference: `restore(completed).with(changesBetween(
 * completed, current))` lands on exactly the parameters `current` holds.
 *
 * The router path has one baseline and passes its changes to `with()`
 * directly, so this stays with the legacy adapters.
 */
export const changesBetween = (
  from: SearchParameters,
  to: SearchParameters
): Partial<SearchParameterValues> => {
  const stated = to.toJSON();
  const changes: Partial<SearchParameterValues> = { ...stated };

  // `Object.keys` widens to `string`; the keys of a parameter bag are names.
  (Object.keys(from.toJSON()) as SearchParameterName[]).forEach((name) => {
    if (!(name in stated)) changes[name] = undefined;
  });

  return changes;
};
